// loomraga-look.js — <lr-look> quick-add + "Add the look" for the Complete-the-look section.
// No external libraries. Mirrors Horizon's own add-to-cart flow so the cart drawer/bubble
// refresh exactly the way the native buttons do: dispatch a CartLinesUpdateEvent carrying a
// deferred promise, POST to the cart, then resolve the promise with the rendered cart
// sections (the drawer morphs itself from detail.sections).
import { CartLinesUpdateEvent } from '@shopify/events';

class LoomragaLook extends HTMLElement {
  connectedCallback() {
    this.busy = false;
    this.status = this.querySelector('[data-lr-status]');
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const single = event.target.closest('[data-lr-add]');
    const look = event.target.closest('[data-lr-add-look]');
    if (single) {
      event.preventDefault();
      this.#add([{ id: single.dataset.variantId, quantity: 1 }], single);
    } else if (look) {
      event.preventDefault();
      this.#addLook(look);
    }
  };

  #addLook(button) {
    const ids = [];
    const costume = this.#costumeVariantId();
    if (costume) ids.push(costume);
    this.querySelectorAll('[data-look-variant]').forEach((el) => ids.push(el.dataset.lookVariant));
    const items = [...new Set(ids)].filter(Boolean).map((id) => ({ id, quantity: 1 }));
    if (items.length) this.#add(items, button);
  }

  // The costume's currently-selected variant on the PDP (falls back to the rendered default).
  #costumeVariantId() {
    const live = document.querySelector(
      'product-form-component [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    if (live && live.value) return live.value;
    return this.dataset.costumeVariant || null;
  }

  async #add(items, button) {
    if (this.busy) return;
    this.busy = true;

    const label = button.querySelector('[data-lr-label]') || button;
    const original = label.textContent;
    button.disabled = true;
    label.textContent = button.dataset.addingLabel || 'Adding…';
    this.#announce('Adding to cart');

    // Cart-drawer section ids so the POST returns rendered HTML the drawer can morph in.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      // Full cart for accurate totals on the bubble; the drawer uses detail.sections.
      const cart = await fetch(`${window.Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: data.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'loomraga-look',
        },
      });

      label.textContent = button.dataset.addedLabel || 'Added ✓';
      this.#announce('Added to cart');
      this.#reset(label, original, button, 1800);
    } catch (err) {
      deferred.reject(err);
      label.textContent = button.dataset.errorLabel || 'Try again';
      this.#announce('Could not add to cart');
      this.#reset(label, original, button, 2400);
      // eslint-disable-next-line no-console
      console.warn('[loomraga-look]', err);
    } finally {
      this.busy = false;
    }
  }

  #reset(label, original, button, delay) {
    setTimeout(() => {
      label.textContent = original;
      button.disabled = false;
    }, delay);
  }

  #announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('lr-look')) {
  customElements.define('lr-look', LoomragaLook);
}
